import './styles/responsive.css';
import Vue from 'vue';
import App from './App.vue';
import router from './router';
import store from './store';
import i18n from './i18n';
import http from './api/http';
import { bindAuth, loadToken, saveToken } from '@shared/auth/http';
import { applyBranding } from '@shared/branding/apply';
import TenantNotFound from './views/TenantNotFound.vue';
import { loadAppearance, applyTheme, applyFontSize, applyColorblind, applyAda } from './utils/appearance';
import focusTrap from './directives/focusTrap';

// Swallow the benign "ResizeObserver loop" notification (see commit history).
const RESIZE_OBSERVER_LOOP_RE =
  /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;
window.addEventListener('error', (e) => {
  if (e && e.message && RESIZE_OBSERVER_LOOP_RE.test(e.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, true);
window.addEventListener('unhandledrejection', (e) => {
  const msg = (e && e.reason && e.reason.message) || '';
  if (RESIZE_OBSERVER_LOOP_RE.test(msg)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

Vue.config.productionTip = false;
Vue.prototype.$http = http;
Vue.directive('focus-trap', focusTrap);

bindAuth(http, { store, router, loginRoute: '/login' });

// Token puente para el learn nuevo embebido (iframe same-origin): si ya hay
// sesión previa (token en localStorage) reescribimos la cookie mu_bridge_token
// en cada arranque, para no depender de un nuevo login. saveToken() la escribe.
// (El learn-app-next la lee en CurrentRootToken.php.)
{
  const bootToken = loadToken();
  if (bootToken) {
    saveToken(bootToken);
  }
}

// White-label (PLS): paint cached branding immediately to avoid a flash of the
// default theme. When logged in, the session branding (authPayload via fetchMe
// below) is authoritative, so we must NOT call the public /api/branding endpoint
// — on new.measureup.com it resolves no tenant and would clobber the active
// organization's branding with the MeasureUp defaults.
applyBranding(store.getters['branding/branding']);
const brandingReady = store.getters['auth/isLoggedIn']
  ? Promise.resolve()
  : store.dispatch('branding/fetchBranding').catch(() => {});

// Apply stored appearance (theme, font size, accessibility) before the first
// render to avoid a flash of the default theme.
const appearance = loadAppearance();
applyTheme(appearance.theme);
applyFontSize(appearance.fontSize);
applyColorblind(appearance.colorblind);
applyAda(appearance.ada);

// Apply the signed-in user's interface language, if we already know it.
const bootUser = store.getters['auth/user'];
if (bootUser && bootUser.locale) {
  const map = { en: 'en', es: 'es', ja: 'ja', 'zh-cn': 'zh', th: 'en' };
  i18n.locale = map[bootUser.locale] || i18n.locale;
}

// If we already have a token from a previous session, refresh user + permissions
// before the first render so router guards don't kick us back to /login.
const bootstrap = store.getters['auth/isLoggedIn']
  ? store.dispatch('auth/fetchMe').catch(() => {})
  : Promise.resolve();

Promise.all([bootstrap, brandingReady]).finally(() => {
  // Si el host trae un subdominio real que no existe en BD, mostramos una
  // pantalla dedicada en vez del portal (evita el flash del login por defecto).
  if (store.getters['branding/tenantMissing']) {
    new Vue({
      i18n,
      render: (h) => h(TenantNotFound, {
        props: { subdomain: store.getters['branding/branding'].subdomain || '' },
      }),
    }).$mount('#app');
    return;
  }

  new Vue({
    router,
    store,
    i18n,
    render: (h) => h(App),
  }).$mount('#app');
});
